import Axios, { AxiosError, AxiosRequestConfig } from 'axios';
import envs from '../../common/envs';
import { getAdminAuthPath } from '../../app/route-utils';

// 토큰 저장소 (TokenManager에서 관리)
export const GlobalAuthInfo: { AccessToken?: string } = {
  AccessToken: '',
};

// Axios 인스턴스 생성 (V2 REST API 전용)
export const AXIOS_INSTANCE = Axios.create({
  baseURL: envs.v2BackendUrl,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 401 에러 핸들링 (토큰 자동 갱신)
AXIOS_INSTANCE.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };
    const requestUrl = String(originalRequest?.url || '');
    const isAuthBootstrapRequest =
      requestUrl.includes('/auth/login') ||
      requestUrl.includes('/auth/google') ||
      requestUrl.includes('/auth/microsoft') ||
      requestUrl.includes('/auth/refresh') ||
      requestUrl.includes('/auth/logout') ||
      requestUrl.includes('/auth/revoke');

    const refreshAccessToken = async (): Promise<string | null> => {
      const currentAccessToken = GlobalAuthInfo.AccessToken;
      const response = await fetch(`${envs.v2BackendUrl}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(currentAccessToken
            ? { Authorization: `Bearer ${currentAccessToken}` }
            : {}),
        },
      });

      if (!response.ok) {
        throw new Error(`Refresh failed: ${response.status}`);
      }

      const data = (await response.json()) as { access_token?: string };
      return data?.access_token || null;
    };

    // 401 에러이고, 재시도하지 않은 경우
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !isAuthBootstrapRequest
    ) {
      originalRequest._retry = true;

      try {
        // V2 REST Refresh 엔드포인트로 토큰 갱신
        const newToken = await refreshAccessToken();
        if (newToken) {
          // 새 토큰 저장
          GlobalAuthInfo.AccessToken = newToken;

          // 원래 요청에 새 토큰 적용
          if (!originalRequest.headers) {
            originalRequest.headers = {};
          }
          originalRequest.headers.Authorization = `Bearer ${newToken}`;

          // 원래 요청 재시도
          return AXIOS_INSTANCE(originalRequest);
        }
      } catch (refreshError) {
        // 토큰 갱신 실패 시 로그아웃
        // eslint-disable-next-line no-console
        console.error('Token refresh failed:', refreshError);
        window.location.href = `${getAdminAuthPath(window.location.pathname)}/logout`;
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// Orval에서 사용할 커스텀 인스턴스
export const customInstance = <T>(
  config: AxiosRequestConfig,
  options?: AxiosRequestConfig,
): Promise<T> => {
  const accessToken = GlobalAuthInfo.AccessToken;
  const controller = new AbortController();

  return AXIOS_INSTANCE({
    ...config,
    ...options,
    signal: controller.signal,
    headers: {
      ...config?.headers,
      ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    paramsSerializer: (params: Record<string, any>) => {
      const searchParams = new URLSearchParams();
      Object.keys(params).forEach((key) => {
        const param = params[key];
        // undefined, null, 빈 문자열은 건너뛰기
        if (param === undefined || param === null || param === '') {
          return;
        }
        // 빈 배열은 건너뛰기
        if (Array.isArray(param) && param.length === 0) {
          return;
        }
        if (Array.isArray(param)) {
          searchParams.append(key, param.join(','));
        } else {
          searchParams.append(key, param);
        }
      });
      return searchParams.toString();
    },
  }).then(({ data }) => {
    // Handle empty response body (Content-Length: 0) as null
    // NestJS returns empty body when controller returns null
    if (data === '' || data === undefined) {
      return null as T;
    }
    return data;
  });
};

export type ErrorType<Error> = AxiosError<Error>;
